import { useTranslation } from 'react-i18next';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  Switch,
  Icon,
  IconButton,
  TooltipRoot,
  TooltipTrigger,
  TooltipContent,
} from '@socialchorus/shared-ui-components';
import { useUserGeneratedContent } from '../../../models/user-generated-contents/context';
import { usePatronSelector } from '../../../common/use-patron-selector';
import { advocateSelectors } from '../../../models/advocate';

const DropdownMenuItemWithSwitch: React.FC<{
  icon: string;
  checked: boolean;
  onCheckedChange: () => void;
  actionName: string;
}> = ({ icon, checked, onCheckedChange, actionName }) => {
  const { t } = useTranslation();
  return (
    <DropdownMenuItem
      onSelect={(e) => {
        e.preventDefault();
        onCheckedChange();
      }}
      className="ugc_sidebar__settings_dropdown_item"
    >
      <div className="ugc_sidebar__settings_dropdown_item_name">
        <Icon>{icon}</Icon>
        <div>{actionName}</div>
      </div>
      <div className="ugc_sidebar__settings_dropdown_item_switch">
        <div aria-hidden className="text-sm">
          {t('common.off')}
        </div>
        <Switch checked={checked} />
        <div aria-hidden className="text-sm">
          {t('common.on')}
        </div>
      </div>
    </DropdownMenuItem>
  );
};

const SettingsDropdown = ({ disabled = false }: { disabled?: boolean }) => {
  const { t } = useTranslation();
  const { userGeneratedContent, updateUserGeneratedContentFormState } =
    useUserGeneratedContent();

  const translationsEnabled = usePatronSelector(
    advocateSelectors.getCanTranslateSubmission
  );
  const commentingEnabled = usePatronSelector(
    advocateSelectors.getCanCommentSubmission
  );
  const sharingEnabled = usePatronSelector(
    advocateSelectors.getCanShareSubmission
  );

  return (
    <TooltipRoot>
      <DropdownMenu>
        {/* Tooltip + dropdown share one trigger via nested asChild; TooltipRoot
            must wrap DropdownMenu so the tooltip's hover handlers reach the trigger. */}
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <IconButton
              iconName="settings"
              size="default"
              disabled={disabled}
              aria-label={t('common.settings')}
            />
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <DropdownMenuContent
          side="bottom"
          align="start"
          className="ugc_sidebar__settings_dropdown"
        >
          {commentingEnabled && (
            <DropdownMenuItemWithSwitch
              icon="mode_comment"
              checked={!!userGeneratedContent.is_commentable}
              onCheckedChange={() => {
                updateUserGeneratedContentFormState({
                  is_commentable: !userGeneratedContent.is_commentable,
                });
              }}
              actionName={t('user_generated_content.commenting')}
            />
          )}
          {sharingEnabled && (
            <DropdownMenuItemWithSwitch
              icon="share"
              checked={!!userGeneratedContent.is_shareable}
              onCheckedChange={() => {
                updateUserGeneratedContentFormState({
                  is_shareable: !userGeneratedContent.is_shareable,
                });
              }}
              actionName={t('user_generated_content.sharing')}
            />
          )}
          {translationsEnabled && (
            <DropdownMenuItemWithSwitch
              icon="translate"
              checked={!!userGeneratedContent.is_translatable}
              onCheckedChange={() => {
                updateUserGeneratedContentFormState({
                  is_translatable: !userGeneratedContent.is_translatable,
                });
              }}
              actionName={t('user_generated_content.translation')}
            />
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {/* py-2 = 8px vertical padding to match Figma; Solar's single-line tooltip defaults to py-1 (4px) */}
      <TooltipContent
        className="py-2"
        description={t('common.settings')}
        sideOffset={5}
      />
    </TooltipRoot>
  );
};

export default SettingsDropdown;
